import moment from "moment";
import TransactionCardSkeleton from "@/components/skeletons/transaction-card-skeleton";
import { useSheetStore } from "@/zustand/sheetStore";
import Pagination from "@/components/pagination";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";
import {
  CARD_TRANSACTION_LABELS,
  formatAmount,
  getCardTransactionDisplayAmount,
  getCardTransactionSign,
  getTransactionStatusMeta,
  type TransactionStatusKey,
} from "@/lib/utils";

const tabToTypeMap: Record<string, string> = {
  deposit: "Deposit",
  withdrawal: "Withdrawal",
  transfer: "Transfer",
  "card-topup": "Card Topup",
  "card-withdrawal": "Card Withdrawal",
  "card-creation": "Card Creation",
  "referral-reward": "Referral Reward",
};

const TransactionCard = ({ transaction }: { transaction: Transaction }) => {
  const formatAmount = useFormatAmountWithCurrency();
  const sign = transaction.type === "deposit" ? "+" : "-";
  const { openSheet } = useSheetStore();
  return (
    <div
      onClick={() =>
        openSheet("transaction-details", null, {
          transactionData: transaction,
          type: "wallet",
        })
      }
      className="text-dark-text-50 border-dark-stroke-5 bg-dark-card-3 space-y-1 rounded-lg border p-3"
    >
      <div className="flex items-center justify-between text-sm">
        <span>{transaction.currency || "USD"}</span>
        <div>
          {sign}
          {formatAmount(transaction.amount)}
        </div>
      </div>
      <div className="text-dark-text-50 flex items-center justify-between text-[10px]">
        <span>{moment(transaction.createdAt).format("DD-MM-YY hh:mm A")}</span>
        <span>{tabToTypeMap[transaction?.type]}</span>
      </div>
      <div className="text-dark-text-50 flex items-center justify-between text-[10px]">
        <span>Id: {transaction?.id}</span>
        <StatusBadge status={transaction.status} />
      </div>
    </div>
  );
};

const CardTransactionCard = ({
  transaction,
}: {
  transaction: CardTransaction;
}) => {
  const { openSheet } = useSheetStore();
  const { amount: displayAmount, currency: displayCurrency } =
    getCardTransactionDisplayAmount(transaction);
  const sign = getCardTransactionSign(
    transaction.type,
    displayAmount,
    transaction.status,
  );

  return (
    <div
      onClick={() =>
        openSheet("transaction-details", null, {
          transactionData: transaction,
          type: "card",
        })
      }
      className="border-dark-stroke-5 bg-dark-card-3 text-dark-text-50 space-y-1 rounded-lg border p-3"
    >
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="min-w-0 flex-1 truncate">
          {transaction.merchantName ||
            CARD_TRANSACTION_LABELS[transaction.type] ||
            transaction.type}
        </span>

        <div className="shrink-0 font-medium whitespace-nowrap">
          {sign}
          {formatAmount(displayAmount)} {displayCurrency?.toUpperCase()}
        </div>
      </div>
      <div className="text-dark-text-50 flex items-center justify-between gap-2 text-[10px]">
        <span className="truncate">
          {moment(transaction.transactionTime).format("DD-MM-YY hh:mm A")}
        </span>
        <StatusBadge status={transaction.status} />
      </div>
    </div>
  );
};

const TransactionList = ({
  transactions,
  isLoading = false,
  type = "wallets",
  handlePageChange,
  totalPages,
  currentPage,
}: {
  transactions: Transaction[] | CardTransaction[];
  isLoading?: boolean;
  type?: "wallets" | "cards";
  handlePageChange?: (page: number) => void;
  totalPages?: number;
  currentPage?: number;
}) => {
  if (isLoading)
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <TransactionCardSkeleton key={index} />
        ))}
      </div>
    );

  if (transactions.length === 0)
    return (
      <div>
        <div className="py-20">
          <p className="text-dark-text-50 text-center">No transactions yet</p>
        </div>
      </div>
    );
  return (
    <div>
      <div className="space-y-4">
        {transactions.map((transaction, index) => {
          const key =
            (transaction as CardTransaction)?.transactionId ??
            transaction?.id ??
            index;

          if (type === "wallets")
            return (
              <TransactionCard
                transaction={transaction as Transaction}
                key={key}
              />
            );

          if (type === "cards")
            return (
              <CardTransactionCard
                transaction={transaction as CardTransaction}
                key={key}
              />
            );
        })}
      </div>

      {(totalPages ?? 0) > 1 && (
        <Pagination
          currentPage={currentPage ?? 1}
          totalPages={totalPages ?? 1}
          onPageChange={handlePageChange as (page: number) => void}
        />
      )}
    </div>
  );
};

interface StatusBadgeProps {
  status?: string | null;
}

const statusStyles: Record<
  TransactionStatusKey,
  { bg: string; textColor: string }
> = {
  closed: { bg: "bg-dark-warning-300", textColor: "text-dark-warning-300" },
  pending: { bg: "bg-dark-warning-300", textColor: "text-dark-warning-300" },
  completed: { bg: "bg-dark-success-300", textColor: "text-dark-success-300" },
  failed: { bg: "bg-dark-error-300", textColor: "text-dark-error-300" },
  rejected: { bg: "bg-dark-error-300", textColor: "text-dark-error-300" },
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const { key, label } = getTransactionStatusMeta(status);
  const { bg, textColor } = statusStyles[key];

  return (
    <span
      className={`inline-flex items-center rounded-full py-1 text-[10px] ${textColor}`}
    >
      <span className={`mr-1 size-1 rounded-full ${bg}`} />
      {label}
    </span>
  );
};

export default TransactionList;
